import { http } from '@/api/http';

/**
 * Raw transport for the catalog-driven configuration store backed by
 * `/api/v1/configs` (PTV2-1067). Admin-only at the controller level; a non-admin
 * caller gets a 403 envelope.
 *
 * Field names below mirror the backend DTOs verbatim, including their
 * `@JsonProperty` overrides (mix of camelCase and snake_case):
 *   - `ConfigDefinitionDTO` → `getCatalog` rows.
 *   - `ConfigItemDTO`       → list / `getConfig` / `upsertConfig` rows.
 * Both serialize `value`/`default` as the CANONICAL STRING form (e.g. DURATION
 * as "PT24H", BOOL as "true"); `default` (JSON key `default`) and `value` are
 * always `null` for secret keys.
 */

export type ConfigType = 'STRING' | 'INT' | 'LONG' | 'BOOL' | 'DURATION' | 'ENUM' | 'URL';
export type ConfigScope = 'TENANT' | 'GLOBAL';
export type ConfigSource = 'DB' | 'ENV' | 'CODE';
export type ConfigExposure = 'ADMIN' | 'AUTHENTICATED';

/** Catalog entry — definition metadata only, no resolved value. */
export interface ConfigDefinitionDto {
  readonly key: string;
  readonly namespace: string;
  readonly type: string;
  readonly scope: string;
  readonly exposure: string;
  readonly secret: boolean;
  readonly tags: readonly string[] | null;
  readonly description: string | null;
  /** Canonical-string code default; `null` when secret. JSON key is `default`. */
  readonly default: string | null;
  /** Allowed values when `type === 'ENUM'`; otherwise `null`. */
  readonly enumValues: readonly string[] | null;
}

/** Effective value row — definition metadata plus the resolved value + source. */
export interface ConfigItemDto {
  readonly key: string;
  readonly namespace: string;
  readonly type: string;
  readonly scope: string;
  readonly exposure: string;
  readonly secret: boolean;
  readonly tags: readonly string[] | null;
  readonly description: string | null;
  /** Canonical-string code default; `null` when secret. JSON key is `default`. */
  readonly default: string | null;
  /** Effective canonical-string value; always `null` for secrets. */
  readonly value: string | null;
  /** Whether an effective value exists (for secrets: whether a stored value is set). */
  readonly isSet: boolean;
  readonly source: string;
  /** `true` iff `source === 'DB'` (a genuine operator override exists). */
  readonly isOverridden: boolean;
  /** ISO-8601 last-modified time of the override row; `null` unless `source === 'DB'`. */
  readonly modified_on: string | null;
  /** Last modifier of the override row; `null` unless `source === 'DB'`. */
  readonly modified_by: string | null;
}

/** Body for `PUT /configs/{key}`. `value` is the canonical string; `reason` is optional audit text. */
export interface UpdateSystemConfigRequestDto {
  readonly value?: string | null;
  readonly reason?: string | null;
}

/** Server-side filters for the list endpoint. Empty/undefined values are dropped by the http layer. */
export interface ListConfigsFilters {
  readonly q?: string;
  readonly namespace?: string;
  readonly tag?: string;
  readonly scope?: string;
  readonly diffOnly?: boolean;
}

export const adminConfigsEndpoints = {
  getCatalog: (options?: { signal?: AbortSignal }): Promise<ConfigDefinitionDto[]> =>
    http.get<ConfigDefinitionDto[]>('/configs/catalog', options),
  listConfigs: (
    filters?: ListConfigsFilters,
    options?: { signal?: AbortSignal },
  ): Promise<ConfigItemDto[]> =>
    http.get<ConfigItemDto[]>('/configs', {
      ...options,
      query: {
        q: filters?.q,
        namespace: filters?.namespace,
        tag: filters?.tag,
        scope: filters?.scope,
        diffOnly: filters?.diffOnly,
      },
    }),
  getConfig: (key: string, options?: { signal?: AbortSignal }): Promise<ConfigItemDto> =>
    http.get<ConfigItemDto>(`/configs/${encodeURIComponent(key)}`, options),
  upsertConfig: (
    key: string,
    body: UpdateSystemConfigRequestDto,
    options?: { signal?: AbortSignal },
  ): Promise<ConfigItemDto> =>
    http.put<ConfigItemDto>(`/configs/${encodeURIComponent(key)}`, body, options),
  resetConfig: (key: string, options?: { signal?: AbortSignal }): Promise<undefined> =>
    http.delete<undefined>(`/configs/${encodeURIComponent(key)}`, options),
  /** Re-encrypt all stored secret values to the active key (requires admin:MANAGE). */
  reencryptSecrets: (options?: { signal?: AbortSignal }): Promise<ReencryptSecretsResultDto> =>
    http.post<ReencryptSecretsResultDto>('/configs/secrets/reencrypt', undefined, options),
};

export interface ReencryptSecretsResultDto {
  readonly reencrypted: number;
}
